// @flow

import * as React from 'react';
import {defineMessages, FormattedMessage} from 'react-intl';
import {CodeTooltip} from '../CodeBlock/CodeTooltip';

export type QuoteType = 'none' | 'single' | 'double';

// Render no, single, or double quotes depending on the codeblock language
const format = (quoteType: QuoteType) => (id: string) =>
  ({
    none: id,
    single: `'${id}'`,
    double: `"${id}"`,
  }[quoteType]);

const tooltips = defineMessages({
  coupon: {
    id: 'docsApp.components.Identifier.coupon',
    defaultMessage: `Create a Coupon with the <a1>API</a1> or through the <a2>Dashboard</a2> before making this request.`,
  },
  customer: {
    id: 'docsApp.components.Identifier.customer',
    defaultMessage: `Create a Customer with the <a1>API</a1> or through the <a2>Dashboard</a2> before making this request.`,
  },
  paymentMethod: {
    id: 'docsApp.components.Identifier.paymentMethod',
    defaultMessage: `Create a PaymentMethod with the <a1>API</a1> or hardcode a <a2>test ID</a2> before making this request.`,
  },
  price: {
    id: 'docsApp.components.Identifier.price',
    defaultMessage: `Create a Price and Product with the <a1>API</a1> or through the <a2>Dashboard</a2> before making this request.`,
  },
  product: {
    id: 'docsApp.components.Identifier.product',
    defaultMessage: `Create a Price and Product with the <a1>API</a1> or through the <a2>Dashboard</a2> before making this request.`,
  },
  partner: {
    id: 'docsApp.components.Identifier.partner',
    defaultMessage: `Find your Partner ID in the Partners section of the <a>Dashboard</a>.`,
  },
  connectedAccount: {
    id: 'docsApp.components.Identifier.connectedAccount',
    defaultMessage: `Create a Connected Account with the <a1>API</a1> before making this request. It is also possible to create a Connected Account through the <a2>Dashboard</a2>, although some Issuing features are not yet accessible.`,
  },
  location: {
    id: 'docsApp.components.Identifier.location',
    defaultMessage: `Create a Location with the <a1>API</a1> or through the <a2>Dashboard</a2> before making this request.`,
  },
  file: {
    id: 'docsApp.components.Identifier.file',
    defaultMessage: `Create a File with the <a>API</a> before making this request.`,
  },
  dispute: {
    id: 'docsApp.components.Identifier.dispute',
    defaultMessage: `Create a Dispute with the <a1>API</a1> or through the <a2>Dashboard</a2> before making this request.`,
  },
  paymentIntent: {
    id: 'docsApp.components.Identifier.paymentIntent',
    defaultMessage: `Create a PaymentIntent with the <a1>API</a1> or hardcode a <a2>test ID</a2> before making this request.`,
  },
  taxCode: {
    id: 'docsApp.components.Identifier.taxCode',
    defaultMessage: `This is the generally taxable tax code for electronically supplied services. You can use <a>other tax codes</a> instead.`,
  },
  account: {
    id: 'docsApp.components.Identifier.account',
    defaultMessage: `Find your Merchant ID by visiting your <a>account settings</a>.`,
  },
});

const CONTENT = {
  customer: {
    id: '{{CUSTOMER_ID}}',
    tooltip: (
      <FormattedMessage
        {...tooltips.customer}
        values={{
          a1: (content) => (
            <a href="/docs/api/customers" target="_blank">
              {content}
            </a>
          ),
          a2: (content) => (
            <a
              href="https://dashboard.stripe.com/test/customers"
              target="_blank"
            >
              {content}
            </a>
          ),
        }}
      />
    ),
  },
  paymentMethod: {
    id: '{{PAYMENT_METHOD_ID}}',
    tooltip: (
      <FormattedMessage
        {...tooltips.paymentMethod}
        values={{
          a1: (content) => (
            <a href="/docs/api/payment_methods" target="_blank">
              {content}
            </a>
          ),
          a2: (content) => (
            <a href="/docs/testing#cards" target="_blank">
              {content}
            </a>
          ),
        }}
      />
    ),
  },
  price: {
    id: '{{PRICE_ID}}',
    tooltip: (
      <FormattedMessage
        {...tooltips.price}
        values={{
          a1: (content) => (
            <a href="/docs/api/prices" target="_blank">
              {content}
            </a>
          ),
          a2: (content) => (
            <a
              href="https://dashboard.stripe.com/test/products"
              target="_blank"
            >
              {content}
            </a>
          ),
        }}
      />
    ),
  },
  product: {
    id: '{{PRODUCT_ID}}',
    tooltip: (
      <FormattedMessage
        {...tooltips.product}
        values={{
          a1: (content) => (
            <a href="/docs/api/products" target="_blank">
              {content}
            </a>
          ),
          a2: (content) => (
            <a
              href="https://dashboard.stripe.com/test/products"
              target="_blank"
            >
              {content}
            </a>
          ),
        }}
      />
    ),
  },
  coupon: {
    id: '{{COUPON_ID}}',
    tooltip: (
      <FormattedMessage
        {...tooltips.coupon}
        values={{
          a1: (content) => (
            <a href="/docs/api/coupons" target="_blank">
              {content}
            </a>
          ),
          a2: (content) => (
            <a href="https://dashboard.stripe.com/test/coupons" target="_blank">
              {content}
            </a>
          ),
        }}
      />
    ),
  },
  partner: {
    id: '{{PARTNER_ID}}',
    tooltip: (
      <FormattedMessage
        {...tooltips.partner}
        values={{
          a: (content) => (
            <a
              href="https://dashboard.stripe.com/partners/settings"
              target="_blank"
            >
              {content}
            </a>
          ),
        }}
      />
    ),
  },
  connectedAccount: {
    id: '{{CONNECTED_ACCOUNT_ID}}',
    tooltip: (
      <FormattedMessage
        {...tooltips.connectedAccount}
        values={{
          a1: (content) => (
            <a href="/docs/api/connected_accounts" target="_blank">
              {content}
            </a>
          ),
          a2: (content) => (
            <a
              href="https://dashboard.stripe.com/test/connect/accounts"
              target="_blank"
            >
              {content}
            </a>
          ),
        }}
      />
    ),
  },
  location: {
    id: '{{LOCATION_ID}}',
    tooltip: (
      <FormattedMessage
        {...tooltips.location}
        values={{
          a1: (content) => (
            <a href="/docs/api/terminal/locations" target="_blank">
              {content}
            </a>
          ),
          a2: (content) => (
            <a
              href="https://dashboard.stripe.com/test/terminal/locations"
              target="_blank"
            >
              {content}
            </a>
          ),
        }}
      />
    ),
  },
  file: {
    id: '{{FILE_ID}}',
    tooltip: (
      <FormattedMessage
        {...tooltips.file}
        values={{
          a: (content) => (
            <a href="/docs/api/files" target="_blank">
              {content}
            </a>
          ),
        }}
      />
    ),
  },
  dispute: {
    id: '{{DISPUTE_ID}}',
    tooltip: (
      <FormattedMessage
        {...tooltips.dispute}
        values={{
          a1: (content) => (
            <a href="/docs/api/disputes" target="_blank">
              {content}
            </a>
          ),
          a2: (content) => (
            <a
              href="https://dashboard.stripe.com/test/disputes"
              target="_blank"
            >
              {content}
            </a>
          ),
        }}
      />
    ),
  },
  paymentIntent: {
    id: '{{PAYMENT_INTENT_ID}}',
    tooltip: (
      <FormattedMessage
        {...tooltips.paymentIntent}
        values={{
          a1: (content) => (
            <a href="/docs/api/payment_intents" target="_blank">
              {content}
            </a>
          ),
          a2: (content) => (
            <a href="/docs/testing#cards" target="_blank">
              {content}
            </a>
          ),
        }}
      />
    ),
  },
  taxCode: {
    id: 'txcd_10000000',
    tooltip: (
      <FormattedMessage
        {...tooltips.taxCode}
        values={{
          a: (content) => (
            <a target="_blank" href="/docs/tax/tax-codes#available-tax-codes">
              {content}
            </a>
          ),
        }}
      />
    ),
  },
  account: {
    id: '{{ACCOUNT_ID}}',
    tooltip: (
      <FormattedMessage
        {...tooltips.account}
        values={{
          a: (content) => (
            <a
              href="https://dashboard.stripe.com/settings/account"
              target="_blank"
            >
              {content}
            </a>
          ),
        }}
      />
    ),
  },
};

type IdentifierResourceType = $Keys<typeof CONTENT>;
type IdentifierProps = {|
  type: IdentifierResourceType,
  value: string,
  quoteType: QuoteType,
|};

export const Identifier = ({type, value, quoteType}: IdentifierProps) => {
  const formatter = format(quoteType);
  const content = CONTENT[type];
  if (!content) {
    // Ideally we should never get here and special case all the resources we need
    return <span>{formatter('{{RESOURCE_ID}}')}</span>;
  }
  return (
    <CodeTooltip content={content.tooltip}>
      {formatter(value || content.id)}
    </CodeTooltip>
  );
};
